import React, { useEffect, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { getGoogleMapsClientConfig } from '@/utils/googleMapsConfig';
import { createQlsMarker } from '@/utils/googleMapsMarkers';
import { decodeGooglePolyline } from '@/utils/googlePolyline';

interface DirectionsRouteMapProps {
  polyline: string;
  origin?: { lat: number; lng: number } | null;
  destination?: { lat: number; lng: number } | null;
  className?: string;
}

async function ensureMapsLoaded(apiKey: string) {
  if (typeof google !== 'undefined' && google.maps?.Map) {
    await google.maps.importLibrary('maps');
    await google.maps.importLibrary('marker');
    return;
  }
  const loader = new Loader({
    apiKey,
    version: 'weekly',
    libraries: ['places', 'marker'],
  });
  await loader.load();
}

const DirectionsRouteMap: React.FC<DirectionsRouteMapProps> = ({
  polyline,
  origin,
  destination,
  className = 'w-full h-64',
}) => {
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mapRef.current || !polyline) {
      return;
    }

    let cancelled = false;
    const path = decodeGooglePolyline(polyline);

    const draw = async () => {
      const { apiKey, mapId } = await getGoogleMapsClientConfig();
      await ensureMapsLoaded(apiKey);
      if (cancelled || !mapRef.current) {
        return;
      }

      const map = new google.maps.Map(mapRef.current, {
        zoom: 12,
        center: path[0] || origin || destination || { lat: 0, lng: 0 },
        gestureHandling: 'cooperative',
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        ...(mapId ? { mapId } : {}),
      });

      new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: '#2563eb',
        strokeOpacity: 0.9,
        strokeWeight: 5,
        map,
      });

      const bounds = new google.maps.LatLngBounds();
      path.forEach((point) => bounds.extend(point));
      if (origin) bounds.extend(origin);
      if (destination) bounds.extend(destination);
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 48);
      }

      if (origin) {
        createQlsMarker({
          map,
          mapId,
          position: origin,
          title: 'Start',
          pin: { fillColor: '#16a34a', labelText: 'A' },
        });
      }
      if (destination) {
        createQlsMarker({
          map,
          mapId,
          position: destination,
          title: 'Destination',
          pin: { fillColor: '#2563eb', labelText: 'B' },
        });
      }
    };

    draw().catch((error) => {
      console.error('Failed to draw directions map', error);
    });

    return () => {
      cancelled = true;
    };
  }, [polyline, origin?.lat, origin?.lng, destination?.lat, destination?.lng]);

  return <div ref={mapRef} className={className} />;
};

export default DirectionsRouteMap;
